/**
 * Этап заказа для карточки «Сейчас»: что происходит, что нажать и где поездка
 * курьера. Только правила — разметка в `OrderNow.tsx`.
 */
import type { IconName } from '../../ui/Icon'
import type { Courier, Delivery, Order, OrderStatus } from './api'
import { pickedCount } from './model'

export interface NextStep {
  label: string
  /** Какую ручку зовём: у каждого шага своя, `status` — только для доски. */
  action: 'take' | 'assemble' | 'call' | 'courier' | 'delivered' | 'issue'
  /**
   * Шаг обычно делает служба доставки. Кнопка есть, только если заявки у
   * службы нет, — иначе статус никто не пришлёт; тогда она тихая, не главная.
   */
  fallback?: boolean
  /** Шаг виден, но пока закрыт: почему — подписью на кнопке не спорим, пишем рядом. */
  blocked?: boolean
}

/**
 * Следующее действие по заказу — одной заметной кнопкой. Подпись и действие
 * заданы вместе, чтобы они не разошлись; порядок всё равно проверяет сервер и
 * отвечает 409, если шаг уже не тот.
 */
export function nextStep(order: Order): NextStep | null {
  const allPicked = order.lines.length > 0 && pickedCount(order) === order.lines.length
  switch (order.status) {
    case 'new':
      return { label: 'Взять в сборку', action: 'take' }
    case 'assembling':
      return {
        label: order.ship === 'delivery' ? 'Собран — вызвать курьера' : 'Собран — готов к выдаче',
        action: 'assemble',
        blocked: !allPicked,
      }
    case 'assembled':
      // Курьера не удалось вызвать при сборке — заявку отправляют заново (R-43).
      return { label: 'Вызвать курьера', action: 'call' }
    // Поездку ведёт служба: её статусы двигают заказ сами (R-44). Нажимать
    // нечего, пока заявка у службы есть.
    case 'awaiting_courier':
      return order.delivery ? null : { label: 'Курьер забрал', action: 'courier', fallback: true }
    case 'in_delivery':
      return order.delivery
        ? null
        : { label: 'Отметить доставленным', action: 'delivered', fallback: true }
    case 'ready_for_pickup':
      return { label: 'Выдать клиенту', action: 'issue' }
    default:
      // «Ожидает оплаты», завершённые и «требует решения»: зависший заказ
      // решают переводом в другую аптеку, а не сборкой (R-38).
      return null
  }
}

/** Что сейчас происходит с заказом — заголовком этапа, по тому, чей ход. */
export function stageTitle(order: Order): string {
  const titles: Record<OrderStatus, string> = {
    awaiting_payment: 'Ждём оплату клиента',
    payment_expired: 'Оплата не пришла',
    new: 'Новый заказ — возьмите в сборку',
    needs_attention: 'Ни одна аптека не может собрать — назначьте аптеку',
    assembling: 'Соберите заказ',
    assembled: order.ship === 'delivery' ? 'Курьер не вызван' : 'Заказ собран',
    awaiting_courier: 'Ждём курьера',
    in_delivery: 'Курьер везёт заказ',
    delivered: 'Доставлен клиенту',
    ready_for_pickup: 'Ждёт клиента в аптеке',
    issued: 'Выдан клиенту',
    reservation_expired: 'Клиент не забрал заказ',
    cancelled: 'Заказ отменён',
  }
  return titles[order.status]
}

/** Этапы поездки курьера: служба присылает их сама (R-44). */
export const TRIP = [
  { status: '', label: 'Ищем водителя' },
  { status: 'courier_assigned', label: 'Едет в аптеку' },
  { status: 'at_pickup', label: 'У аптеки' },
  { status: 'picked_up', label: 'Везёт клиенту' },
  { status: 'delivered', label: 'Доставлено' },
]

/**
 * Где сейчас поездка. Заказ мог уйти дальше, чем успела сообщить служба
 * (кнопкой «Курьер забрал»), — тогда шкала не отстаёт от заказа.
 */
export function tripStep(order: Order, delivery: Delivery | null): number {
  const reported = TRIP.findIndex((step) => step.status === (delivery?.status ?? ''))
  const floor = order.status === 'delivered' ? 4 : order.status === 'in_delivery' ? 3 : 0
  return Math.max(reported, floor)
}

/** Значок этапа: что сейчас с заказом, одним взглядом. */
export function stageIcon(order: Order): IconName {
  const icons: Record<OrderStatus, IconName> = {
    awaiting_payment: 'i-card',
    payment_expired: 'i-card',
    new: 'i-box',
    needs_attention: 'i-alert',
    assembling: 'i-box',
    assembled: order.ship === 'delivery' ? 'i-alert' : 'i-check',
    awaiting_courier: 'i-truck',
    in_delivery: 'i-truck',
    delivered: 'i-check',
    ready_for_pickup: 'i-pin',
    issued: 'i-check',
    reservation_expired: 'i-clock',
    cancelled: 'i-x',
  }
  return icons[order.status]
}

/**
 * Ход не за аптекой: ждём оплату, курьера или конец поездки. Эти этапы
 * сменятся сами — экран показывает, что он ждёт, а не застыл.
 */
export function waitsOutside(status: OrderStatus): boolean {
  return status === 'awaiting_payment' || status === 'awaiting_courier' || status === 'in_delivery'
}

/**
 * Кто везёт. Служба присылает машину раньше имени, а иногда имени нет вовсе:
 * машина или номер уже значат, что водитель назначен.
 */
export function courierTitle(courier: Courier | null): string {
  if (courier?.name) return courier.name
  if (courier?.car) return courier.car
  if (courier?.carNumber || courier?.phone) return 'Водитель назначен'
  return 'Водитель ещё не назначен'
}
